import { authService } from './auth.service';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import type {
  ApiResponse,
  Prompt,
  RSSFeed,
  CreateFeedRequest,
  UpdateFeedRequest,
  TestFeedResponse,
  Job,
  CreateJobRequest,
  JobProgress,
  JobStatus,
  FeedsListResponse,
  JobsListResponse
} from '../types/api';

class DashboardService {
  // ============================================================================
  // Prompt Management (Simple - Single Prompt)
  // ============================================================================

  /**
   * Get or create the user's default prompt
   * Call this on dashboard load
   */
  async getOrCreateDefaultPrompt(): Promise<Prompt> {
    try {
      // Try to get existing default prompt
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PROMPTS_DEFAULT}`, {
        headers: authService.getAuthHeaders()
      });

      const data: ApiResponse<{ prompt: Prompt }> = await response.json();

      if (data.success && data.data) {
        return data.data.prompt;
      }
    } catch (error) {
      console.log('No default prompt found, creating one...');
    }

    // No default prompt exists, create one
    return this.createDefaultPrompt();
  }

  /**
   * Create the first default prompt
   */
  private async createDefaultPrompt(): Promise<Prompt> {
    const defaultPromptText = `Analyze this article for relevance to artificial intelligence and technology applications.

Consider the following factors:
1. Mentions of AI technologies (machine learning, deep learning, NLP, computer vision)
2. Real-world applications and use cases
3. Technical depth and implementation details
4. Business impact and measurable outcomes
5. Innovation and significance

Rate the article from 0-100 based on:
- 0-30: Not relevant or minimal AI/tech content
- 31-60: Somewhat relevant, mentions AI/tech but lacks depth
- 61-85: Highly relevant with substantial AI/tech content and insights
- 86-100: Exceptional content with deep technical insights and real-world impact

Provide a brief explanation (2-3 sentences) for your score.`;

    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PROMPTS}`, {
      method: 'POST',
      headers: authService.getAuthHeaders(),
      body: JSON.stringify({
        name: 'My Analysis Criteria',
        description: 'AI and Technology Relevance Filter',
        prompt_text: defaultPromptText,
        is_default: true
      })
    });

    const data: ApiResponse<{ prompt: Prompt }> = await response.json();

    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to create prompt');
    }

    return data.data.prompt;
  }

  /**
   * Update prompt text when user edits it
   */
  async updatePrompt(promptId: string, updates: { name?: string; prompt_text?: string; description?: string }): Promise<Prompt> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PROMPT_BY_ID(promptId)}`, {
      method: 'PUT',
      headers: authService.getAuthHeaders(),
      body: JSON.stringify(updates)
    });

    const data: ApiResponse<{ prompt: Prompt }> = await response.json();

    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to update prompt');
    }

    return data.data.prompt;
  }

  // ============================================================================
  // RSS Feed Management
  // ============================================================================

  /**
   * Get all feeds (optionally filter by active status)
   */
  async getFeeds(activeOnly = false): Promise<RSSFeed[]> {
    const url = activeOnly
      ? `${API_BASE_URL}${API_ENDPOINTS.FEEDS}?active=true`
      : `${API_BASE_URL}${API_ENDPOINTS.FEEDS}`;

    const response = await fetch(url, {
      headers: authService.getAuthHeaders()
    });

    const data: ApiResponse<FeedsListResponse> = await response.json();

    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to get feeds');
    }

    return data.data.feeds;
  }

  /**
   * Create new RSS feed
   */
  async createFeed(feed: CreateFeedRequest): Promise<RSSFeed> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.FEEDS}`, {
      method: 'POST',
      headers: authService.getAuthHeaders(),
      body: JSON.stringify(feed)
    });

    const data: ApiResponse<{ feed: RSSFeed }> = await response.json();

    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to create feed');
    }

    return data.data.feed;
  }

  /**
   * Update RSS feed
   */
  async updateFeed(feedId: string, updates: UpdateFeedRequest): Promise<RSSFeed> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.FEED_BY_ID(feedId)}`, {
      method: 'PUT',
      headers: authService.getAuthHeaders(),
      body: JSON.stringify(updates)
    });

    const data: ApiResponse<{ feed: RSSFeed }> = await response.json();

    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to update feed');
    }

    return data.data.feed;
  }

  /**
   * Delete RSS feed
   */
  async deleteFeed(feedId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.FEED_BY_ID(feedId)}`, {
      method: 'DELETE',
      headers: authService.getAuthHeaders()
    });

    const data: ApiResponse = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to delete feed');
    }
  }

  /**
   * Test feed URL before creating
   */
  async testFeed(url: string): Promise<TestFeedResponse> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.FEEDS_TEST}`, {
      method: 'POST',
      headers: authService.getAuthHeaders(),
      body: JSON.stringify({ url })
    });

    const data: ApiResponse<TestFeedResponse> = await response.json();

    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to test feed');
    }

    return data.data;
  }

  // ============================================================================
  // Analysis Jobs
  // ============================================================================

  /**
   * Create and start a new analysis job
   * Uses default prompt and all active feeds if not specified
   */
  async createJob(options?: CreateJobRequest): Promise<Job> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.JOBS}`, {
      method: 'POST',
      headers: authService.getAuthHeaders(),
      body: JSON.stringify({
        min_relevance_score: 50,
        export_format: 'html',
        include_full_content: true,
        ...options
      })
    });

    const data: ApiResponse<{ job: Job }> = await response.json();

    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to create job');
    }

    return data.data.job;
  }

  /**
   * Get job status
   */
  async getJob(jobId: string): Promise<Job> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.JOB_BY_ID(jobId)}`, {
      headers: authService.getAuthHeaders()
    });

    const data: ApiResponse<{ job: Job }> = await response.json();

    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to get job');
    }

    return data.data.job;
  }

  /**
   * Track job progress using polling
   * Returns a stop function to stop polling
   */
  trackJobProgress(
    jobId: string,
    onProgress: (progress: JobProgress) => void,
    onError?: (error: Error) => void
  ): () => void {
    let intervalId: NodeJS.Timeout | null = null;
    let stopped = false;

    const pollProgress = async () => {
      if (stopped) return;

      try {
        const response = await fetch(
          `${API_BASE_URL}${API_ENDPOINTS.JOB_PROGRESS(jobId)}`,
          {
            headers: authService.getAuthHeaders()
          }
        );

        if (!response.ok) {
          console.error('Progress polling failed:', response.status);
          return;
        }

        const data: ApiResponse<{ progress: JobProgress }> = await response.json();

        if (data.success && data.data) {
          const progress = data.data.progress;
          onProgress(progress);

          // Stop polling when job is done
          if (['completed', 'failed', 'cancelled'].includes(progress.status)) {
            if (intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
            stopped = true;
          }
        }
      } catch (error) {
        console.error('Error polling progress:', error);
        if (onError && error instanceof Error) {
          onError(error);
        }
      }
    };

    // Initial call
    pollProgress();

    // Start polling every 10 seconds to minimize API load
    // For a 2-minute job: ~12 requests instead of 24
    intervalId = setInterval(pollProgress, 10000);

    // Return stop function
    return () => {
      stopped = true;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
  }

  /**
   * List user's jobs
   */
  async listJobs(status?: JobStatus, limit = 20, offset = 0): Promise<JobsListResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      ...(status && { status })
    });

    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.JOBS}?${params}`, {
      headers: authService.getAuthHeaders()
    });

    const data: ApiResponse<JobsListResponse> = await response.json();

    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to list jobs');
    }

    return data.data;
  }

  /**
   * Cancel running job
   */
  async cancelJob(jobId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.JOB_BY_ID(jobId)}`, {
      method: 'DELETE',
      headers: authService.getAuthHeaders()
    });

    const data: ApiResponse = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to cancel job');
    }
  }
}

export const dashboardService = new DashboardService();
